import { AudienceEstimateIcon, RefreshIcon } from '@/lib/Icons';
import { TrashIcon } from '@heroicons/react/outline';
import { filter, isEmpty, map, reduce, values } from 'lodash';
import { FC, Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { ConditionSelector, ExpressionConditionRender } from '..';
import { Expression, ExpressionCondition } from '../../../../models/audiences';
import { AudienceRule, Condition } from '../../../../models/rules';
import { ToastType } from '../../../../models/toast';
import { RootState, useAppDispatch } from '../../../../reducers';
import { useGetPropertyOperationsQuery, usePostEstimateMutation } from '../../../../services/endpoints/audiences';
import { showToast } from '../../../toasts/toastsSlice';
import { useWorkspace } from '../../../workspaces/hooks';
import { stashAudience } from '../../audienceEditSlice';

function mapRulesToAudienceExpression(rules: AudienceRule[]): Record<string, Expression> {
  return reduce<AudienceRule, Record<string, Expression>>(
    rules,
    (result, rule, ruleIndex) => ({
      ...result,
      [String(ruleIndex)]: reduce(
        rule.expressions,
        (result, expression, expressionIndex) => ({
          ...result,
          [String(expressionIndex)]: expression,
        }),
        {}
      ),
    }),
    {}
  );
}

function mapAudienceExpressionToRules(expression?: Record<string, Expression>): AudienceRule[] {
  return map(values(expression), (exp, index) => ({
    id: String(index),
    expressions: values(exp),
  }));
}

export const ExpressionContainer: FC = () => {
  const dispatch = useAppDispatch();

  const { t } = useTranslation('audience_edit');
  const { t: tAudience } = useTranslation('audience');

  const audience = useSelector((state: RootState) => state.audienceEdit.audience);
  const workspace = useWorkspace();

  const { data: { propertyOperations } = {} } = useGetPropertyOperationsQuery({
    workspaceId: workspace.id,
    audienceId: audience?.id,
  });

  const [rules, setRules] = useState<AudienceRule[]>(mapAudienceExpressionToRules(audience?.expression));
  const [canEvaluate, setEvaluate] = useState(false);

  const [getEstimate, { isLoading, data, isSuccess, isError }] = usePostEstimateMutation();

  useEffect(() => {
    if (audience?.id && audience?.expression) {
      getEstimate({
        workspaceId: workspace.id,
        audienceId: audience.id,
        audience: {
          expression: audience.expression,
        },
      });
    }
  }, []);

  const handleAddConditionButtonClicked = (ruleId: string, condition: Condition) => {
    setRules(
      map(rules, (r) =>
        r.id === ruleId
          ? {
            ...r,
            expressions: [
              ...r.expressions,
              {
                type: condition.kind === 'EVENT' || condition.kind === 'VISIT' ? 'ACTION' : condition.kind,
                kind: condition.kind === 'EVENT' || condition.kind === 'VISIT' ? condition.kind : undefined,
              },
            ],
          }
          : r
      )
    );
  };

  const handleRemoveExpressionButtonClicked = (ruleId: string, expressionId: number) => {
    setRules(
      filter(
        map(rules, (r) =>
          r.id === ruleId
            ? {
              ...r,
              expressions: filter(r.expressions, (_, index) => index !== expressionId),
            }
            : r
        ),
        (r) => !isEmpty(r.expressions)
      )
    );
  };

  const handleOnNewRuleSelected = (condition: Condition) => {
    setRules([
      ...rules,
      {
        id: String(rules.length + 1),
        expressions: [
          {
            type: condition.kind === 'EVENT' || condition.kind === 'VISIT' ? 'ACTION' : condition.kind,
            kind: condition.kind === 'EVENT' || condition.kind === 'VISIT' ? condition.kind : undefined,
          },
        ],
      },
    ]);
  };

  const handleExpressionChanged = (ruleId: string, expression: ExpressionCondition, expressionId: number) => {
    setRules(
      filter(
        map(rules, (r) =>
          r.id === ruleId
            ? {
              ...r,
              expressions: map(r.expressions, (exp, index) => (index === expressionId ? expression : exp)),
            }
            : r
        ),
        (r) => !isEmpty(r.expressions)
      )
    );
  };

  useEffect(() => {
    dispatch(
      stashAudience({
        ...audience,
        expression: mapRulesToAudienceExpression(rules),
      })
    );

    setEvaluate(true);
  }, [rules]);

  useEffect(() => {
    if (!isLoading && (isSuccess || isError)) {
      setEvaluate(false);
    }
  }, [isLoading, isSuccess, isError]);

  const handleEvaluateClicked = () => {
    if (audience?.id && audience?.expression) {
      const isErrorArray = Object.values(audience.expression).flatMap((or) => {
        return Object.values(or).map((rule) => {
          switch (rule.type) {
            case 'PROPERTY':
              return rule.comparisonOp !== 'NULL' && rule.comparisonOp !== 'NOT_NULL' && (rule.value === null || rule.value === '');
            default:
              return false;
          }
        });
      });
      if (isErrorArray.find((e) => e)) {
        dispatch(
          showToast({
            type: ToastType.WARNING,
            title: t('step.audience.evaluation.invalid_rules'),
            message: t('step.audience.evaluation.invalid_rules_message'),
          })
        );
      } else {
        getEstimate({
          workspaceId: workspace.id,
          audienceId: audience.id,
          audience: {
            expression: audience.expression,
          },
        });
      }
    }
  };

  return (
    <div>
      <div className='mt-12 flex place-content-between gap-8 align-top'>
        <div>
          <h3 className='text-left text-base font-bold'>{t('step.rules.title')}</h3>
          <p className={'text-left'}>{t('step.rules.description')}</p>
        </div>
        <div className='group flex flex-col items-end justify-end'>
          <div className='flex flex-col gap-3 rounded-lg border border-secondary-border p-6'>
            <h3 className='font-medium text-secondary-700 '>{t('step.rules.recalculate_title')}</h3>
            <div className='flex items-center justify-between gap-3'>
              <div className='flex items-center gap-3'>
                <span className='rounded-full bg-brand-100 p-2.5 text-brand-600'>
                  <AudienceEstimateIcon className='size-5' />
                </span>
                <p className='text-3xl font-semibold text-brand-600'>{data?.size || 0}</p>
              </div>
              <button
                className={`border-0 bg-transparent text-brand-500 flex items-center gap-1`}
                onClick={handleEvaluateClicked}
                title={t(canEvaluate ? 'rules.buttons.recalculate' : 'rules.buttons.modify_to_recalculate')}
              >
                <span>{t('rules.buttons.recalculate')}</span>
                <RefreshIcon className={`size-4 ${isLoading && 'animate-spin'}`} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <h3 className='mb-4 mt-12 text-left font-semibold text-brand-600'>{t('step.rules.the_user')}</h3>
      <div className='space-y-4'>
        {rules.map((rule, ruleIndex) => (
          <Fragment key={ruleIndex}>
            {ruleIndex > 0 && (
              <div className='my-6 flex items-center gap-3'>
                <div className='whitespace-nowrap text-sm text-quaternary-500'>{tAudience('rules.and')}</div>
                <div className='h-px flex-1 bg-quaternary-200'></div>
              </div>
            )}

            <div className='space-y-4 rounded-md border border-secondary-border p-4'>
              {rule.expressions.map((expression, expressionIndex) => (
                <Fragment key={expressionIndex}>
                  {expressionIndex > 0 && (
                    <div className='my-4 flex items-center gap-3'>
                      <div className='whitespace-nowrap text-sm text-quaternary-500'>{tAudience('rules.or')}</div>
                      <div className='h-px flex-1 bg-quaternary-200'></div>
                    </div>
                  )}

                  <div className='flex items-start gap-3'>
                    <div className='flex-1'>
                      <ExpressionConditionRender
                        expression={expression}
                        datasourceIds={audience?.datasourceIds}
                        onChange={(exp) => handleExpressionChanged(rule.id, exp, expressionIndex)}
                        operationList={propertyOperations}
                      />
                    </div>
                    <button
                      className='shrink-0 rounded-md text-gray-800 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-700 pt-2'
                      onClick={() => handleRemoveExpressionButtonClicked(rule.id, expressionIndex)}
                    >
                      <TrashIcon className='size-6 text-brand-600' aria-hidden='true' />
                    </button>
                  </div>
                </Fragment>
              ))}

              <div className='my-4'>
                <div className='mb-2 flex items-center gap-3'>
                  <div className='whitespace-nowrap text-sm text-quaternary-500'>{tAudience('rules.or')}</div>
                  <div className='h-px flex-1 bg-quaternary-200'></div>
                </div>
                <ConditionSelector
                  onConditionSelected={(condition) => handleAddConditionButtonClicked(rule.id, condition)}
                  datasourceIds={audience?.datasourceIds}
                />
              </div>
            </div>
          </Fragment>
        ))}

        {!isEmpty(rules) && (
          <div className='my-6 flex items-center gap-3'>
            <div className='whitespace-nowrap text-sm text-quaternary-500'>{tAudience('rules.and')}</div>
            <div className='h-px flex-1 bg-quaternary-200'></div>
          </div>
        )}

        <div className='mb-3'>
          <ConditionSelector onConditionSelected={handleOnNewRuleSelected} isFirstCondition={isEmpty(rules)} datasourceIds={audience?.datasourceIds} />
          {isEmpty(rules) && <p className='mt-1 text-sm text-quaternary-500'>{t('rules.buttons.add_condition_description')}</p>}
        </div>
      </div>
    </div>
  );
};
